import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
  useRef,
} from "react";
import { ChatSession, ChatMessage, Organization } from "../types/chatTypes";
import { chatSessionService } from "../services/ChatSessionService";
import { flowiseService } from "../services/FlowiseService";
import { organizationRouter } from "../../organizations/services/organizationRouter";
import { Farm } from "../../../services/farm/farmService";
import { useAuth } from "../../../hooks/useAuth";
import { titleGenerationService } from "../../../services/titleGenerationService";

// ✅ NOVO: vamos acionar o painel da direita através do DashboardContext
// OBS: ajuste o caminho relativo se o seu projeto tiver uma estrutura diferente
import { useDashboard } from "../../../contexts/DashboardContext";

interface ChatContextType {
  sessions: ChatSession[];
  activeSessionId: string | null;
  organizations: Farm[];
  selectedOrganization: Farm | null;
  setSelectedOrganization: (org: Farm | null) => void;
  messages: Record<string, ChatMessage[]>;
  isProcessing: boolean;
  isChatDisabled: boolean;
  isMultiTaskRunning: boolean;
  setActiveSession: (sessionId: string | null) => Promise<void>;
  createSession: (organizationId: string, title?: string, organizationOverride?: Farm) => Promise<string>;
  sendMessage: (content: string, selectedFlowId?: string, force?: boolean, silent?: boolean) => Promise<void>;
  submitMissingFieldsForCurrentTask: (values: Record<string, any>) => Promise<void>;
  sendManagerMessageWithContext: (content: string, selectedFlowId: string, extraContext: Record<string, any>) => Promise<void>;
  handleRetryDecision: (decision: 'yes' | 'no', taskContent: string, taskIndex?: number, silent?: boolean) => Promise<void>;
  retrySingleMessage: (decision: 'yes' | 'no', assistantMessageId: string) => Promise<void>;
  loadSessions(): Promise<void>;
  loadMessages: (sessionId: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  loadOrganizations(): Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
  children: ReactNode;
}

// ✅ Helper: tenta extrair JSON de uma string (inclusive se vier “embrulhado”)
function tryParseJson<T = any>(maybeJson: string): T | null {
  if (!maybeJson || typeof maybeJson !== "string") return null;
  // tentativa direta
  try {
    return JSON.parse(maybeJson) as T;
  } catch (_) {
    // tentativa: achar o primeiro bloco {...} válido dentro da string
    const firstBrace = maybeJson.indexOf("{");
    const lastBrace = maybeJson.lastIndexOf("}");
    if (firstBrace >= 0 && lastBrace > firstBrace) {
      const slice = maybeJson.slice(firstBrace, lastBrace + 1);
      try {
        return JSON.parse(slice) as T;
      } catch (_) {
        return null;
      }
    }
    return null;
  }
}

// ✅ Helper: detecta “consulta_animal” e retorna o melhor identificador
function extractAnimalIdFromResponse(
  contentText: string,
  rawResponse?: any
): string | undefined {
  // 1) tenta pelo rawResponse (quando o FlowiseService conseguiu parsear)
  const rawData = rawResponse?.data;
  if (rawData?.operation_code === "consulta_animal") {
    // prioriza campos comuns para id/brinco
    return (
      rawData.animalId ||
      rawData.id ||
      rawData.name || // seu exemplo usa "name": "863"
      rawData.tag ||
      rawData.brinco ||
      undefined
    );
  }

  // 2) tenta parsear o texto da mensagem (quando veio como JSON string)
  const parsed = tryParseJson<any>(contentText);
  const data = parsed?.data;
  if (data?.operation_code === "consulta_animal") {
    return (
      data.animalId ||
      data.id ||
      data.name ||
      data.tag ||
      data.brinco ||
      undefined
    );
  }

// sem sinal de consulta de animal
return undefined;
}

// ✅ Helper: detecta respostas com status INCOMPLETO e extrai campos faltantes
function detectIncompleteFromResponse(
  messageText?: string,
  raw?: any
): { missing_fields: string[]; resumo?: string; operation_code?: string; proximo_campo?: string } | null {
  try {
    const parsed = messageText ? tryParseJson<any>(messageText) : null;
    const candidates: any[] = [];
    if (raw && typeof raw === 'object') {
      candidates.push(raw, raw.data, raw.response, raw?.data?.data);
    }
    if (parsed && typeof parsed === 'object') {
      candidates.push(parsed, parsed.data);
    }

    for (const src of candidates) {
      if (!src || typeof src !== 'object') continue;
      const status = src.status || src?.data?.status;
      const missing = src.missing_fields || src?.data?.missing_fields || [];
      const resumo = src.resumo || src?.data?.resumo;
      const op = src.operation_code || src?.data?.operation_code;
      const nextField = src.proximo_campo || src?.data?.proximo_campo;
      const hasMissing = Array.isArray(missing) && missing.length > 0;
      const isIncomplete = typeof status === 'string' && status.toUpperCase() === 'INCOMPLETO';
      if (isIncomplete || hasMissing) {
        return {
          missing_fields: Array.isArray(missing) ? missing : [],
          resumo,
          operation_code: op,
          proximo_campo: nextField,
        };
      }
    }

    // Fallback: heurística baseada em string
    const txt = (messageText || '').toLowerCase();
    if (txt.includes('incompleto') || txt.includes('missing_fields')) {
      return {
        missing_fields: [],
        resumo: undefined,
        operation_code: undefined,
        proximo_campo: undefined,
      };
    }
  } catch (_) {
    // silencioso
  }
  return null;
}

// Tenta extrair pares campo->valor de uma mensagem livre do usuário
function parseMissingFieldsFromUserMessage(
  message: string,
  expectedFields: string[]
): Record<string, any> {
  const values: Record<string, any> = {};
  const text = (message || "").trim();
  if (!text) return values;

  // 1) Se for JSON, utilizar chaves diretamente
  const parsed = tryParseJson<any>(text);
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    for (const f of expectedFields) {
      if (Object.prototype.hasOwnProperty.call(parsed, f)) {
        values[f] = parsed[f];
      }
    }
    if (Object.keys(values).length > 0) return values;
  }

  // 2) Analisar padrões simples: "campo: valor", "campo = valor", "campo valor"
  const parts = text.replace(/\n/g, ";").split(/[;,]+/).map(p => p.trim()).filter(Boolean);
  for (const part of parts) {
    for (const f of expectedFields) {
      const r1 = new RegExp(`^${f}\\s*[:=]\\s*(.+)$`, "i");
      const r2 = new RegExp(`^${f}\\s+(.+)$`, "i");
      let m = part.match(r1) || part.match(r2);
      if (m && m[1]) {
        const v = m[1].trim();
        values[f] = v;
        break;
      }
    }
  }

  // 3) Caso haja apenas um campo esperado, mapear texto cru
  if (Object.keys(values).length === 0 && expectedFields.length === 1) {
    values[expectedFields[0]] = text;
  }

  return values;
}

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Farm[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<Farm | null>(
    null
  );
  const [messages, setMessages] = useState<Record<string, ChatMessage[]>>({});
  const [loading, setLoading] = useState(false);
  const { authState } = useAuth();

  // ✅ NOVO: acesso ao updateContent do painel direito
  const { updateContent, contentView } = useDashboard();
  const [isChatDisabled, setIsChatDisabled] = useState(false);

  const isLabView = (view?: string) => {
    if (!view) return false;
    return [
      "examsHub",
      "mastitisIntro",
      "mastitisTeatStep",
      "mastitisAnimalIdStep",
      "mastitisTestStep1",
      "mastitisTestStep2",
      "mastitisTestStep3",
      "exam-list",
      "exam-detail",
    ].includes(view);
  };

  useEffect(() => {
    setIsChatDisabled(isLabView(contentView));
  }, [contentView]);

  // Controle interno para evitar reentrância ao processar multi-tarefas
  const multiTaskRunningRef = useRef(false);
  const [isMultiTaskRunning, setIsMultiTaskRunning] = useState(false);
  // Refs auxiliares para controle de INCOMPLETO e fila multitarefas
  const lastAssistantResponseRef = useRef<{ content: string; raw?: any } | null>(null);
  const multiTaskTasksRef = useRef<string[]>([]);
  const multiTaskStatusesRef = useRef<("pending" | "done" | "error")[]>([]);
  const pendingInputResolverRef = useRef<(() => void) | null>(null);
  const pendingInputContextRef = useRef<{ operation_code?: string; currentTaskIndex?: number } | null>(null);
  const pendingRetryResolverRef = useRef<(() => void) | null>(null);
  const pendingRetryContextRef = useRef<{ currentTaskIndex?: number } | null>(null);
  const currentTaskIndexRef = useRef<number | null>(null);

  // ✅ Calcular se há mensagens sendo processadas - otimizado
  const isProcessing = useMemo(() => {
    if (!activeSessionId) return false;
    const sessionMessages = messages[activeSessionId];
    if (!sessionMessages || sessionMessages.length === 0) return false;
    return sessionMessages.some(msg => msg.status === 'sending');
  }, [activeSessionId, activeSessionId ? messages[activeSessionId] : null]);

  // Load user sessions and organizations when authenticated and username available
  useEffect(() => {
    const username = authState.user?.username;

    // Always load organizations (may be public)
    loadOrganizations();

    // Only load sessions after auth is ready and username is known
    if (authState.isAuthenticated && username) {
      import('../services/ChatSessionService')
        .then(module => {
          module.updateChatSessionUsername(username);
        })
        .catch(err => console.error('Failed to update chat session username:', err))
        .finally(() => {
          // Now load sessions for the authenticated user
          loadSessions();
        });
    }
  }, [authState.isAuthenticated, authState.user?.username]);

  // Function to convert Farm to Organization
  const farmToOrganization = (farm: Farm): Organization => {
    const farmKey = (farm as any)._key;
    const farmIdFallback = farm.id;
    const nameBasedId = farm.name.toLowerCase().replace(/\s+/g, "-");
    const farmId = farmKey || farmIdFallback || nameBasedId;
    
    return {
      id: farmId,
      name: farm.name,
      type: "farm" as const,
      description: `Fazenda ${farm.farm_type} - ${farm.location.region}${
        farm.area_hectares ? ` (${farm.area_hectares} hectares)` : ""
      }${
        farm.production_focus
          ? ` | Foco: ${farm.production_focus}`
          : ""
      }${farm.contact_info?.phone ? ` | Tel: ${farm.contact_info.phone}` : ""}`,
      location: `${farm.location.address}, ${farm.location.region}${
        farm.location.postal_code ? ` - ${farm.location.postal_code}` : ""
      }`,
    };
  };

  const loadOrganizations = async () => {
    try {
      const ownerIds = [authState.profileId, authState.user?.id].filter(Boolean) as string[];
      
      if (ownerIds.length === 0) {
        setOrganizations([]);
        return;
      }
      
      const organizationsByOwner = await Promise.all(
        ownerIds.map((id) => organizationRouter.getAllOrganizations(id))
      );
      const orgs = organizationsByOwner.flat();
      const unique = new Map<string, Farm>();
      orgs.forEach((farm) => {
        const key = (farm as any)._key || farm.id || farm.name;
        if (!unique.has(key)) {
          unique.set(key, farm);
        }
      });
      const uniqueOrgs = Array.from(unique.values());
       
      const mappedOrgs = uniqueOrgs.map(farm => {
        const organization = farmToOrganization(farm);
        return {
          ...farm,
          ...organization,
        };
      });
       
      setOrganizations(mappedOrgs as unknown as Farm[]);
    } catch (error) {
      console.error('Erro ao carregar organizações:', error);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => {
      loadOrganizations();
    };
    window.addEventListener('muu:organizations:refresh', handler);
    return () => {
      window.removeEventListener('muu:organizations:refresh', handler);
    };
  }, [loadOrganizations]);

  const loadSessions = async (): Promise<void> => {
    // Carregando sessões do usuário
    setLoading(true);
    try {
      const result = await chatSessionService.getUserSessions();
      if (result.success && result.data) {
        // Mesclar sessões do servidor com sessões locais existentes
        setSessions(prevSessions => {
          const serverSessions = result.data || [];
          const localSessionIds = prevSessions.map(s => s.session_id);
          
          // Adicionar apenas sessões do servidor que não existem localmente
          const newServerSessions = serverSessions.filter(
            serverSession => !localSessionIds.includes(serverSession.session_id)
          );
          

          
          // Retornar sessões locais + novas do servidor
          return [...prevSessions, ...newServerSessions];
        });
      } else {
        // Nenhuma sessão encontrada no servidor - manter sessões locais

      }
    } catch (error) {
      console.error("Failed to load sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (sessionId: string): Promise<void> => {
    if (messages[sessionId]) return; // Already loaded

    try {
      const result = await chatSessionService.getMessages(sessionId);

      if (result.success && result.data && result.data.length > 0) {
        setMessages((prev) => ({
          ...prev,
          [sessionId]: result.data || [],
        }));
      } else {
        // Create welcome message if no messages exist
        const session = sessions.find((s) => s.session_id === sessionId);
        const organization = organizations.find(
          (o) => o.id === session?.organization_id
        );

        // Gerar sender_id dinâmico baseado nos gestores reais
        const userId = (organization as any)?.owner_id;
        const dynamicSender = await generateDynamicSender(undefined, undefined, userId);

        const welcomeMessage: ChatMessage = {
          id: `welcome_${sessionId}`,
          message_id: `welcome_${sessionId}`,
          session_id: sessionId,
          content: `Olá! Eu sou o MUU, seu assistente agropecuário para ${
            organization?.name || "esta organização"
          }. Como posso ajudá-lo hoje?`,
          sender: "assistant",
          sender_type: dynamicSender.sender_type,
          sender_id: dynamicSender.sender_id,
          timestamp: new Date().toISOString(),
          message_type: "text",
          status: "sent",
        };

        // Save and set welcome message
        await chatSessionService.saveMessage(welcomeMessage);
        setMessages((prev) => ({
          ...prev,
          [sessionId]: [welcomeMessage],
        }));
      }
    } catch (error) {
      console.error("Error loading messages:", error);
      // Set empty array on error
      setMessages((prev) => ({
        ...prev,
        [sessionId]: [],
      }));
    }
  };

  const setActiveSession = async (sessionId: string | null): Promise<void> => {
    setActiveSessionId(sessionId);

    if (!sessionId) return;

    try {
      const session = sessions.find((s) => s.session_id === sessionId);

      // Buscar mensagens diretamente para obter contagem exata imediatamente
      const result = await chatSessionService.getMessages(sessionId);
      const directCount = result.success && Array.isArray(result.data) ? result.data.length : 0;

      // Prime cache se ainda não carregado
      if (!messages[sessionId]) {
        if (directCount > 0) {
          setMessages((prev) => ({
            ...prev,
            [sessionId]: result.data || [],
          }));
        } else {
          // Carregar mensagens (irá criar welcome se necessário)
          await loadMessages(sessionId);
        }
      }

      // ✅ Reset painel direito para 'welcome' apenas em nova conversa de fazenda
      if (session?.organization_type === 'farm' && directCount <= 1) {
        updateContent('welcome');
      }
    } catch (e) {
      console.warn('Falha ao atualizar painel direito para welcome:', e);
    }

    // Update session timestamp in background
    chatSessionService.touchSession(sessionId).catch(console.error);
  };

  const createSession = async (
    organizationId: string,
    title?: string,
    organizationOverride?: Farm
  ): Promise<string> => {
    const organization = organizationOverride || organizations.find((o) => {
      return o.id === organizationId || (o as any)._key === organizationId;
    });
    
    if (!organization) {
      throw new Error(`Organization not found: ${organizationId}`);
    }

    const result = await chatSessionService.createSession({
      organizationId,
      organizationType:
        "type" in organization && typeof organization.type === "string"
          ? organization.type
          : "farm", // Default to 'farm' since most organizations are farms
      title: title || `Conversa - ${organization.name}`,
    });

    if (!result.success || !result.data) {
      throw new Error(result.error || "Failed to create session");
    }

    // Add to local state
    setSessions((prev) => [result.data!, ...prev]);

    // Set active session and reset right panel for farm sessions
    const createdSessionId = result.data.session_id;
    setActiveSessionId(createdSessionId);

    try {
      // Use the organization type to decide initial panel
      const organizationObj = organizationOverride || organizations.find((o) => {
        return o.id === organizationId || (o as any)._key === organizationId;
      });
      const orgType = (organizationObj && typeof (organizationObj as any).type === 'string')
        ? (organizationObj as any).type
        : 'farm';

      if (orgType === 'farm') {
        updateContent('welcome');
      }

      // Prime messages cache for the new session (creates welcome if empty)
      await loadMessages(createdSessionId);
    } catch (e) {
      console.warn('Falha ao inicializar UI da nova sessão:', e);
    }

    return createdSessionId;
  };

  const sendMessage = async (
    content: string,
    selectedFlowId?: string,
    force?: boolean,
    silent?: boolean
  ): Promise<void> => {
    if (!activeSessionId || isProcessing || (isChatDisabled && !force)) return;

    const isMultiTaskOperation = multiTaskRunningRef.current || multiTaskTasksRef.current.includes(content);

    let userMessage: ChatMessage | null = null;
    if (!silent) {
      userMessage = {
        message_id: `msg_${Date.now()}_user`,
        session_id: activeSessionId,
        sender_type: "user",
        sender_id: authState.user?.username || "anonymous",
        content,
        timestamp: new Date().toISOString(),
        message_type: "text",
        id: `msg_${Date.now()}_user`,
        sender: "user",
        status: "sent",
        metadata: isMultiTaskOperation ? { isMultitaskTask: true } : undefined,
      };
      setMessages((prev) => ({
        ...prev,
        [activeSessionId]: [...(prev[activeSessionId] || []), userMessage as ChatMessage],
      }));
    }

    const tempAssistantMessage: ChatMessage = {
      id: `msg_${Date.now()}_assistant`,
      message_id: `msg_${Date.now()}_assistant`,
      session_id: activeSessionId,
      content: "Pensando...",
      sender: "assistant",
      sender_type: "assistant",
      sender_id: "muu_assistant",
      timestamp: new Date().toISOString(),
      message_type: "text",
      status: "sending",
    };

    setMessages((prev) => ({
      ...prev,
      [activeSessionId]: [
        ...(prev[activeSessionId] || []),
        tempAssistantMessage,
      ],
    }));

    try {
      if (userMessage) {
        await chatSessionService.saveMessage(userMessage);

        const session = sessions.find((s) => s.session_id === activeSessionId);
        if (session && titleGenerationService.isConfigured()) {
          try {
            const newTitle = await titleGenerationService.generateTitle(activeSessionId, content);

            if (newTitle && newTitle !== session.title) {
              await chatSessionService.updateSession(activeSessionId, { title: newTitle });

              setSessions((prev) =>
                prev.map((s) =>
                  s.session_id === activeSessionId ? { ...s, title: newTitle } : s
                )
              );
            }
          } catch (error) {
            console.error("Erro ao gerar/atualizar título da sessão:", error);
          }
        }
      }
    } catch (error) {
      console.error("Error saving user message:", error);
    }

    

    try {
      // Enviando mensagem na sessão ativa
      
      // Get the active session
      const session = sessions.find((s) => s.session_id === activeSessionId);
      if (!session) {
        throw new Error("Session not found");
      }
      
      // Get the selected organization to extract owner_id
      const activeOrganization = organizations.find(org => 
        (org as any)._key === session.organization_id || 
        org.id === session.organization_id
      );
      
      // ✅ Process message through Flowise (interceptando entradas de campos faltantes via chat)
      let flowIdToUse = selectedFlowId;
      let questionToSend = content;
      let extraContext: Record<string, any> = {
        owner_id: (activeOrganization as any)?.owner_id,
        profileId: authState.profileId,
        farmId: session.organization_id,
        ...(multiTaskRunningRef.current ? { multitask: true } : {}),
      };

      const pending = pendingInputContextRef.current;
      if (pending && multiTaskRunningRef.current) {
        const last = lastAssistantResponseRef.current;
        const incomplete = detectIncompleteFromResponse(last?.content, last?.raw);
        if (incomplete && Array.isArray(incomplete.missing_fields) && incomplete.missing_fields.length > 0) {
          const values = parseMissingFieldsFromUserMessage(content, incomplete.missing_fields);
          questionToSend = JSON.stringify({
            operation_code: incomplete.operation_code || pending.operation_code,
            dados: values,
          });
          flowIdToUse = "master"; // envia diretamente ao gestor master
          extraContext = { ...extraContext, multitask: true };
        }
      }

      const result = await flowiseService.processMessage({
        question: questionToSend,
        sessionId: activeSessionId,
        organizationId: session.organization_id || undefined,
        organizationType: session.organization_type || undefined,
        selectedFlowId: flowIdToUse, // ✅ Flow ID ajustado conforme cenário
        context: extraContext,
      });

      

      // Gerar sender_id dinâmico baseado nos gestores reais
      const userId = (activeOrganization as any)?.owner_id;
      const dynamicSender = await generateDynamicSender(
        result.response?.flowUsed?.id || flowIdToUse,
        result.response?.flowUsed?.name,
        userId
      );

      const assistantMessage: ChatMessage = {
        ...tempAssistantMessage,
        content: result.success && result.response ? result.response.text : 'Desculpe, ocorreu um erro ao processar sua solicitação.',
        status: result.success ? 'sent' : 'error',
        sender_id: dynamicSender.sender_id,
        sender_type: dynamicSender.sender_type,
        metadata: {
          organizationId: session.organization_id,
          organizationType: session.organization_type,
          flowUsedId: result.response?.flowUsed?.id || selectedFlowId || null,
          flowUsedName: result.response?.flowUsed?.name || null,
          ...(isMultiTaskOperation ? { isMultitaskTask: true } : {}),
          ...(isMultiTaskOperation ? { multitaskTaskContent: questionToSend } : {}),
          ...(typeof currentTaskIndexRef.current === 'number' ? { multitaskTaskIndex: currentTaskIndexRef.current } : {}),
        },
      };

      // Guarda última resposta do assistente para inspeção posterior
      lastAssistantResponseRef.current = {
        content: assistantMessage.content,
        raw: result.response?.rawResponse,
      };

      setMessages((prev) => ({
        ...prev,
        [activeSessionId]: prev[activeSessionId].map((msg) =>
          msg.id === tempAssistantMessage.id ? assistantMessage : msg
        ),
      }));

      // Save assistant message to database
      try {
        await chatSessionService.saveMessage(assistantMessage);
      } catch (error) {
        console.error("Error saving assistant message:", error);
      }

      // Após salvar a resposta do assistente, tratar caso de campos faltantes enviados via chat
      try {
        const pending = pendingInputContextRef.current;
        if (pending && multiTaskRunningRef.current) {
          const last = lastAssistantResponseRef.current;
          const incompleteNow = detectIncompleteFromResponse(last?.content, last?.raw);
          if (incompleteNow) {
            // Continua incompleto: atualiza UI com novos campos faltantes
            try {
              updateContent('multiTaskProgress', {
                tasks: multiTaskTasksRef.current,
                statuses: multiTaskStatusesRef.current,
                pendingInput: {
                  ...incompleteNow,
                  currentTaskIndex: pending.currentTaskIndex,
                },
              });
            } catch (uiErr) {
              console.warn('Falha ao atualizar UI (incompleto via chat):', uiErr);
            }
          } else {
            // Concluiu: marca task como concluída e libera continuação da fila
            const idx = pending.currentTaskIndex;
            if (typeof idx === 'number' && idx >= 0 && idx < multiTaskStatusesRef.current.length) {
              multiTaskStatusesRef.current[idx] = 'done';
            }
            try {
              updateContent('multiTaskProgress', {
                tasks: multiTaskTasksRef.current,
                statuses: multiTaskStatusesRef.current,
                pendingInput: undefined,
              });
            } catch (uiUpdErr) {
              console.warn('Falha ao atualizar status de multitarefa após envio via chat:', uiUpdErr);
            }
            if (pendingInputResolverRef.current) {
              const resolve = pendingInputResolverRef.current;
              pendingInputResolverRef.current = null;
              pendingInputContextRef.current = null;
              resolve();
              if (pendingRetryResolverRef.current) {
                const r = pendingRetryResolverRef.current;
                pendingRetryResolverRef.current = null;
                pendingRetryContextRef.current = null;
                r();
              }
            }
          }
        }
      } catch (mtErr) {
        console.warn('Falha ao tratar envio de campos via chat:', mtErr);
      }

      // Após salvar a resposta do assistente, detectar e processar multi-tarefas
      try {
        const normalizeTasks = (input: any): string[] => {
          if (!input) return [];
          // Caso seja um array: pode conter strings ou objetos com { tasks: string[] }
          if (Array.isArray(input)) {
            const directStrings = input
              .filter((t: any) => typeof t === "string")
              .map((t: string) => t.trim())
              .filter((t: string) => t.length > 0);
            const nestedStrings = input
              .flatMap((item: any) => Array.isArray(item?.tasks) ? item.tasks : [])
              .filter((t: any) => typeof t === "string")
              .map((t: string) => t.trim())
              .filter((t: string) => t.length > 0);
            return [...directStrings, ...nestedStrings];
          }
          // Caso seja um objeto com propriedade tasks
          if (input && Array.isArray(input.tasks)) {
            return input.tasks
              .filter((t: any) => typeof t === "string")
              .map((t: string) => t.trim())
              .filter((t: string) => t.length > 0);
          }
          return [];
        };

        const extractTasksFromPayload = (messageText: string, raw?: any): string[] | null => {
          
          const rawUrl = raw?.url || raw?.data?.url || raw?.metadata?.url || raw?.data?.metadata?.url || raw?.response?.metadata?.url;
          const rawTasksInput = raw?.tasks || raw?.data?.tasks || raw?.metadata?.tasks || raw?.data?.metadata?.tasks || raw?.response?.metadata?.tasks;
          if (typeof (rawUrl || "") === "string") {
            const u = String(rawUrl).toLowerCase();
            if (u.includes("master/tasks")) {
              const tasks = normalizeTasks(rawTasksInput);
              if (tasks.length > 0) return tasks;
            }
          }

          let parsedRawText: any = null;
          if (raw && typeof raw?.text === "string") {
            parsedRawText = tryParseJson<any>(raw.text);
          }
          if (parsedRawText && typeof parsedRawText === "object") {
            const urlFromRawText = parsedRawText?.metadata?.url || parsedRawText?.url || parsedRawText?.data?.url;
            const tasksFromRawText = parsedRawText?.metadata?.tasks || parsedRawText?.tasks || parsedRawText?.data?.tasks;
            if (typeof (urlFromRawText || "") === "string") {
              const u = String(urlFromRawText).toLowerCase();
              const tasks = normalizeTasks(tasksFromRawText);
              if (u.includes("master/tasks") && tasks.length > 0) return tasks;
            }
          }

          const parsed = tryParseJson<any>(messageText);
          const url = parsed?.metadata?.url || parsed?.url || parsed?.data?.url;
          const tasksInput = parsed?.metadata?.tasks || parsed?.tasks || parsed?.data?.tasks;
          if (typeof (url || "") === "string") {
            const u = String(url).toLowerCase();
            if (u.includes("master/tasks")) {
              const tasks = normalizeTasks(tasksInput);
              if (tasks.length > 0) return tasks;
            }
          }
          return null;
        };

        const tasks = extractTasksFromPayload(assistantMessage.content, result.response?.rawResponse);
        if (tasks && tasks.length > 0) {
          // Evitar reentrância caso já esteja rodando uma fila
            if (!multiTaskRunningRef.current) {
              multiTaskRunningRef.current = true;
              setIsMultiTaskRunning(true);
            // Inicializa UI de progresso com todas as tarefas pendentes
            const initialStatuses = tasks.map(() => "pending" as const);
            try {
              updateContent('multiTaskProgress', { tasks, statuses: initialStatuses });
            } catch (uiErr) {
              console.warn('Falha ao atualizar UI de multitarefas:', uiErr);
            }
            // Guarda listas em refs para controle de INCOMPLETO
            multiTaskTasksRef.current = tasks;
            multiTaskStatusesRef.current = [...initialStatuses];
            // Pequeno atraso para garantir que isProcessing seja atualizado para false
            setTimeout(async () => {
              try {
                const statuses: ("pending"|"done"|"error")[] = [...initialStatuses];
                for (let i = 0; i < tasks.length; i++) {
                  const task = tasks[i];
                  // Garantir leve intervalo entre envios
                  await new Promise((r) => setTimeout(r, 100));
                  // Enviar cada task pelo gestor master
                  currentTaskIndexRef.current = i;
                  await sendMessage(task, "master");
                  // Após a resposta, checar se a tarefa está incompleta
                  const last = lastAssistantResponseRef.current;
                  const incomplete = detectIncompleteFromResponse(last?.content, last?.raw);
                  if (incomplete) {
                    // Pausa a fila e exibe formulário para coleta de dados
                    pendingInputContextRef.current = { operation_code: incomplete.operation_code, currentTaskIndex: i };
                    try {
                      updateContent('multiTaskProgress', {
                        tasks,
                        statuses,
                        pendingInput: {
                          ...incomplete,
                          currentTaskIndex: i,
                        },
                      });
                    } catch (uiUpdErr) {
                      console.warn('Falha ao atualizar UI (incompleto):', uiUpdErr);
                    }
                    // Aguarda usuário preencher os campos
                    await new Promise<void>((resolve) => {
                      pendingInputResolverRef.current = resolve;
                    });
                    // Sincroniza com o ref (pode ter sido atualizado no submit)
                    statuses[i] = multiTaskStatusesRef.current[i];
                  } else {
                    const isError = typeof (last?.content || '') === 'string' && /Desculpe, ocorreu um erro/i.test(last!.content as string);
                    const newStatus = isError ? "error" as const : "done" as const;
                    statuses[i] = newStatus;
                    multiTaskStatusesRef.current[i] = newStatus;
                    try {
                      updateContent('multiTaskProgress', { tasks, statuses });
                    } catch (uiUpdErr) {
                      console.warn('Falha ao atualizar status de multitarefa:', uiUpdErr);
                    }
                    if (newStatus === 'error') {
                      pendingRetryContextRef.current = { currentTaskIndex: i };
                      await new Promise<void>((resolve) => {
                        pendingRetryResolverRef.current = resolve;
                      });
                      statuses[i] = multiTaskStatusesRef.current[i];
                      try {
                        updateContent('multiTaskProgress', { tasks, statuses });
                      } catch (_) {}
                    }
                    try {
                      const nestedTasks = extractTasksFromPayload(last?.content || '', last?.raw);
                      if (nestedTasks && nestedTasks.length > 0) {
                        const normalizedNested = nestedTasks
                          .filter((t: any) => typeof t === 'string')
                          .map((t: string) => t.trim())
                          .filter((t: string) => t.length > 0);
                        if (normalizedNested.length > 0) {
                          const nestedStatuses = normalizedNested.map(() => "pending" as const);
                          tasks.splice(i + 1, 0, ...normalizedNested);
                          statuses.splice(i + 1, 0, ...nestedStatuses);
                          multiTaskTasksRef.current = tasks;
                          multiTaskStatusesRef.current = statuses;
                          try {
                            updateContent('multiTaskProgress', { tasks, statuses });
                          } catch (uiNestedErr) {
                            console.warn('Falha ao atualizar UI (nested multitarefas):', uiNestedErr);
                          }
                        }
                      }
                    } catch (nestedErr) {
                      console.warn('Falha ao detectar mult-tarefas aninhadas:', nestedErr);
                    }
                  }
                }
              } catch (queueErr) {
                console.error("Erro ao processar fila de multi-tarefas:", queueErr);
              } finally {
                multiTaskRunningRef.current = false;
                setIsMultiTaskRunning(false);
                try {
                  const tasks = [...(multiTaskTasksRef.current || [])];
                  const statuses = [...(multiTaskStatusesRef.current || [])];
                  pendingInputResolverRef.current = null;
                  pendingInputContextRef.current = null;
                  pendingRetryResolverRef.current = null;
                  pendingRetryContextRef.current = null;
                  currentTaskIndexRef.current = null;
                  updateContent('multiTaskProgress', { tasks, statuses, pendingInput: undefined });
                } catch (_) {}
              }
            }, 50);
          }
        }
      } catch (mtErr) {
        console.warn("Falha ao avaliar multi-tarefas:", mtErr);
      }

      // Atualiza status de tarefa específica quando o usuário clica em "tentar novamente"
      try {
        if (!multiTaskRunningRef.current) {
          const idx = multiTaskTasksRef.current.findIndex((t) => t === content);
          if (idx >= 0) {
            const last = lastAssistantResponseRef.current;
            const incompleteNow = detectIncompleteFromResponse(last?.content, last?.raw);
            if (incompleteNow) {
              multiTaskStatusesRef.current[idx] = 'pending';
              updateContent('multiTaskProgress', {
                tasks: multiTaskTasksRef.current,
                statuses: multiTaskStatusesRef.current,
                pendingInput: { ...incompleteNow, currentTaskIndex: idx },
              });
            } else {
              const isErr = /Desculpe, ocorreu um erro/i.test(assistantMessage.content || '') || assistantMessage.status === 'error';
              multiTaskStatusesRef.current[idx] = isErr ? 'error' : 'done';
              updateContent('multiTaskProgress', {
                tasks: multiTaskTasksRef.current,
                statuses: multiTaskStatusesRef.current,
                pendingInput: undefined,
              });
            }
          }
        }
      } catch (mtSingleErr) {
        console.warn('Falha ao atualizar status de tarefa única:', mtSingleErr);
      }

      // ✅ NOVO: tentar detectar se a resposta indica “consulta_animal”
      if (result.success && result.response) {
        const animalId = extractAnimalIdFromResponse(
          assistantMessage.content,
          result.response.rawResponse
        );
        if (animalId && !multiTaskRunningRef.current && !isMultiTaskOperation) {
          // 👉 Troca o painel da direita para o AnimalPassport
          // Extrair dados completos do animal da resposta
          const animalData = result.response.rawResponse?.data || 
                           tryParseJson(assistantMessage.content)?.data;
          
          // 👉 Troca o painel da direita para o AnimalPassport com dados completos
          updateContent("animalPassport", { animalData, animalId });
        }
      }

      // Update session last used
      await chatSessionService.touchSession(activeSessionId);
    } catch (error) {
      // Update message with error status
      const errorMessage = {
        ...tempAssistantMessage,
        content: "Erro ao enviar mensagem",
        status: "error" as const,
      };

      setMessages((prev) => ({
        ...prev,
        [activeSessionId]: prev[activeSessionId].map((msg) =>
          msg.id === tempAssistantMessage.id ? errorMessage : msg
        ),
      }));

      // Save error message to database
      try {
        await chatSessionService.saveMessage(errorMessage);
      } catch (saveError) {
        console.error("Error saving error message:", saveError);
      }
    }
  };

  const deleteSession = async (sessionId: string): Promise<void> => {
    const result = await chatSessionService.deleteSession(sessionId);
    if (result.success) {
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
      setMessages((prev) => {
        const newMessages = { ...prev };
        delete newMessages[sessionId];
        return newMessages;
      });

      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
      }
    }
  };

  const contextValue: ChatContextType = {
    sessions,
    activeSessionId,
    organizations,
    selectedOrganization,
    setSelectedOrganization,
    messages,
    isProcessing,
    isChatDisabled,
    isMultiTaskRunning,
    setActiveSession,
    createSession,
    sendMessage,
    submitMissingFieldsForCurrentTask: async (values: Record<string, any>) => {
      // Envia complementos de campos para a tarefa atual incompleta e gerencia retomada da fila
      try {
        const pending = pendingInputContextRef.current;
        if (!pending) return;
        const { operation_code, currentTaskIndex } = pending;

        // Monta payload estruturado, mantendo compatibilidade com o gestor master
        const payload = JSON.stringify({
          operation_code: operation_code || undefined,
          dados: values,
        });

        // Envia ao gestor master com flag multitask
        await sendMessage(payload, "master");

        // Verifica se ainda está incompleto
        const last = lastAssistantResponseRef.current;
        const incomplete = detectIncompleteFromResponse(
          last?.content,
          last?.raw
        );

        if (incomplete) {
          // Atualiza UI com novos campos faltantes e mantém pausa da fila
          try {
            updateContent('multiTaskProgress', {
              tasks: multiTaskTasksRef.current,
              statuses: multiTaskStatusesRef.current,
              pendingInput: {
                ...incomplete,
                currentTaskIndex,
              },
            });
          } catch (uiErr) {
            console.warn('Falha ao atualizar UI de multitarefa (incompleto):', uiErr);
          }
          return; // Continua aguardando novos complementos
        }

        // Concluiu: marca a tarefa como concluída, remove pendência e retoma fila
        if (
          typeof currentTaskIndex === 'number' &&
          currentTaskIndex >= 0 &&
          currentTaskIndex < (multiTaskStatusesRef.current?.length || 0)
        ) {
          multiTaskStatusesRef.current[currentTaskIndex] = 'done';
        }

        try {
          updateContent('multiTaskProgress', {
            tasks: multiTaskTasksRef.current,
            statuses: multiTaskStatusesRef.current,
            pendingInput: undefined,
          });
        } catch (uiUpdErr) {
          console.warn('Falha ao atualizar status de multitarefa após complemento:', uiUpdErr);
        }

        // Notifica o laço da fila para continuar
        if (pendingInputResolverRef.current) {
          const resolve = pendingInputResolverRef.current;
          pendingInputResolverRef.current = null;
          pendingInputContextRef.current = null;
          resolve();
        }
      } catch (err) {
        console.error('Erro ao enviar campos faltantes:', err);
      }
    },
    handleRetryDecision: async (decision: 'yes' | 'no', taskContent: string, taskIndex?: number, silent?: boolean) => {
      try {
        const idxFromContent = typeof taskIndex === 'number' ? taskIndex : multiTaskTasksRef.current.indexOf(taskContent);
        const idx = idxFromContent >= 0 ? idxFromContent : (pendingRetryContextRef.current?.currentTaskIndex ?? -1);
        if (idx < 0) return;
        const tasks = [...multiTaskTasksRef.current];
        const statuses = [...multiTaskStatusesRef.current];
        if (decision === 'no') {
          multiTaskStatusesRef.current[idx] = 'error';
          try {
            updateContent('multiTaskProgress', { tasks, statuses: multiTaskStatusesRef.current });
          } catch (_) {}
          if (pendingRetryResolverRef.current) {
            const r = pendingRetryResolverRef.current;
            pendingRetryResolverRef.current = null;
            pendingRetryContextRef.current = null;
            r();
          }
          return;
        }
        multiTaskStatusesRef.current[idx] = 'pending';
        try {
          updateContent('multiTaskProgress', { tasks, statuses: multiTaskStatusesRef.current });
        } catch (_) {}
        currentTaskIndexRef.current = idx;
        await sendMessage(taskContent, 'master', undefined, silent ?? true);
        const last = lastAssistantResponseRef.current;
        const incomplete = detectIncompleteFromResponse(last?.content, last?.raw);
        if (incomplete) {
          pendingInputContextRef.current = { operation_code: incomplete.operation_code, currentTaskIndex: idx };
          try {
            updateContent('multiTaskProgress', {
              tasks,
              statuses: multiTaskStatusesRef.current,
              pendingInput: { ...incomplete, currentTaskIndex: idx },
            });
          } catch (_) {}
          return;
        }
        const isErr = typeof (last?.content || '') === 'string' && /Desculpe, ocorreu um erro/i.test(last!.content as string);
        multiTaskStatusesRef.current[idx] = isErr ? 'error' : 'done';
        try {
          updateContent('multiTaskProgress', { tasks, statuses: multiTaskStatusesRef.current });
        } catch (_) {}
        if (!isErr && pendingRetryResolverRef.current) {
          const r = pendingRetryResolverRef.current;
          pendingRetryResolverRef.current = null;
          pendingRetryContextRef.current = null;
          r();
        }
      } catch (err) {
        console.warn('Falha na decisão de retry:', err);
      }
    },
    sendManagerMessageWithContext: async (content: string, selectedFlowId: string, extraContext: Record<string, any>) => {
      if (!activeSessionId) return;

      // Temporary assistant message
      const tempAssistantMessage: ChatMessage = {
        id: `msg_${Date.now()}_assistant_ctx`,
        message_id: `msg_${Date.now()}_assistant_ctx`,
        session_id: activeSessionId,
        content: 'Processando decisão...',
        sender: 'assistant',
        sender_type: 'assistant',
        sender_id: 'muu_assistant',
        timestamp: new Date().toISOString(),
        message_type: 'text',
        status: 'sending',
      };
      setMessages((prev) => ({
        ...prev,
        [activeSessionId]: [...(prev[activeSessionId] || []), tempAssistantMessage],
      }));

      try {
        const session = sessions.find((s) => s.session_id === activeSessionId);
        if (!session) throw new Error('Session not found');
        const activeOrganization = organizations.find(org => (org as any)._key === session.organization_id || org.id === session.organization_id);

        const result = await flowiseService.processMessage({
          question: content,
          sessionId: activeSessionId,
          organizationId: session.organization_id || undefined,
          organizationType: session.organization_type || undefined,
          selectedFlowId,
          context: {
            owner_id: (activeOrganization as any)?.owner_id,
            profileId: (activeOrganization as any)?.owner_id || undefined,
            farmId: session.organization_id,
            ...extraContext,
          },
        });

        const assistantMessage: ChatMessage = {
          ...tempAssistantMessage,
          content: result.success && result.response ? result.response.text : 'Desculpe, ocorreu um erro ao processar sua solicitação.',
          status: result.success ? 'sent' : 'error',
          sender_id: tempAssistantMessage.sender_id,
          sender_type: tempAssistantMessage.sender_type as 'assistant',
          metadata: {
            organizationId: session.organization_id,
            organizationType: session.organization_type,
            flowUsedId: result.response?.flowUsed?.id || selectedFlowId || null,
            flowUsedName: result.response?.flowUsed?.name || null,
          },
        };

        setMessages((prev) => ({
          ...prev,
          [activeSessionId]: prev[activeSessionId].map((m) => (m.id === tempAssistantMessage.id ? assistantMessage : m)),
        }));
        try {
          await chatSessionService.saveMessage(assistantMessage);
        } catch (error) {
          console.error('Error saving assistant message:', error);
        }
        await chatSessionService.touchSession(activeSessionId);
      } catch (error) {
        const errorMessage: ChatMessage = {
          ...tempAssistantMessage,
          content: 'Erro ao enviar decisão',
          status: 'error',
        } as const;
        setMessages((prev) => ({
          ...prev,
          [activeSessionId]: prev[activeSessionId].map((m) => (m.id === tempAssistantMessage.id ? errorMessage : m)),
        }));
        try {
          await chatSessionService.saveMessage(errorMessage);
        } catch (saveError) {
          console.error('Error saving error message:', saveError);
        }
      }
    },
    retrySingleMessage: async (decision: 'yes' | 'no', assistantMessageId: string) => {
      try {
        if (decision !== 'yes') return;
        if (!activeSessionId) return;
        const sessionMessages = messages[activeSessionId] || [];
        const idx = sessionMessages.findIndex(m => (m.id || m.message_id) === assistantMessageId);
        if (idx < 0) return;
        let prevUserContent: string | null = null;
        for (let i = idx - 1; i >= 0; i--) {
          const m = sessionMessages[i];
          if ((m.sender === 'user') || (m.sender_type === 'user')) {
            prevUserContent = m.content;
            break;
          }
        }
        if (!prevUserContent) return;
        const assistantMsg = sessionMessages[idx];
        const usedFlow = (assistantMsg?.metadata as any)?.flowUsedId || 'master';
        await sendMessage(prevUserContent, usedFlow, true);
      } catch (err) {
        console.warn('Falha no retry de mensagem única:', err);
      }
    },
    loadSessions,
    loadMessages,
    deleteSession,
    loadOrganizations,
  };

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
};

export const useChatContext = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};

/**
 * Gera sender_id e sender_type dinâmicos baseados nos gestores reais do sistema
 */
async function generateDynamicSender(flowId?: string, flowName?: string, userId?: string) {
  try {
    // Buscar gestores reais do sistema
    const availableFlows = await flowiseService.getAvailableFlows(userId);
    
    if (!flowId && !flowName) {
      // Se não tem flow específico, usar o primeiro gestor disponível ou fallback
      const firstFlow = availableFlows[0];
      // Converter o ID de volta para manager_type original
      const originalManagerType = getOriginalManagerType(firstFlow?.id);
      return {
        sender_id: originalManagerType || "muu_assistant",
        sender_type: "assistant" as const
      };
    }

    // Tentar encontrar o flow pelos gestores reais
    let targetFlow = null;

    // 1. Buscar por ID exato
    if (flowId) {
      targetFlow = availableFlows.find(flow => flow.id === flowId);
    }

    // 2. Se não encontrou por ID, buscar por nome
    if (!targetFlow && flowName) {
      targetFlow = availableFlows.find(flow => 
        flow.name.toLowerCase().includes(flowName.toLowerCase()) ||
        flowName.toLowerCase().includes(flow.name.toLowerCase())
      );
    }

    // 3. Se ainda não encontrou, tentar buscar por palavras-chave
    if (!targetFlow && (flowId || flowName)) {
      const searchTerm = (flowId || flowName || '').toLowerCase();
      targetFlow = availableFlows.find(flow => {
        const flowIdLower = flow.id.toLowerCase();
        const flowNameLower = flow.name.toLowerCase();
        
        return flowIdLower.includes(searchTerm) || 
               flowNameLower.includes(searchTerm) ||
               searchTerm.includes(flowIdLower) ||
               searchTerm.includes(flowNameLower);
      });
    }

    // Converter o ID de volta para manager_type original
    const originalManagerType = getOriginalManagerType(targetFlow?.id);
    
    // Retornar o sender_id do gestor encontrado ou fallback
    return {
      sender_id: originalManagerType || "muu_assistant",
      sender_type: "assistant" as const
    };
  } catch (error) {
    console.error('Erro ao buscar gestores dinâmicos:', error);
    // Fallback em caso de erro
    return {
      sender_id: "muu_assistant",
      sender_type: "assistant" as const
    };
  }
}

/**
 * Converte o ID simplificado de volta para o manager_type original
 */
function getOriginalManagerType(flowId?: string): string | null {
  if (!flowId) return null;
  
  const reverseTypeMap: Record<string, string> = {
    'dairy': 'gestor_ordenha',
    'animal': 'gestor_perfil_animal',
    'health': 'gestor_sanidade',
    'nutrition': 'gestor_nutricao',
    'permissions': 'gestor_permissoes',
    'batch': 'gestor_lotes',
    'pastos': 'gestor_pastos',
    'reproducao': 'gestor_reproducao',
    'laboratory': 'gestor_laboratorial',
    'master': 'gestor_master'
  };
  
  return reverseTypeMap[flowId] || flowId;
}
